import noContent from '@/assets/images/no-content.webp';
import { Block, IconButton, IconFont, Spin } from '@/components';
import {
  getAnnotatedList,
  getTaggingList,
  removeTasks,
} from '@/services/custom/extractConfig';
import type { INote } from '@/services/custom/type';
import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { Link, useMutation, useQuery, useSearchParams } from '@umijs/max';
import type { ModalFuncProps } from 'antd';
import { App, Button, Form, Input, Select, Table } from 'antd';
import type { FC } from 'react';
import { useContext, useEffect, useState } from 'react';
import { readonlyContext } from '../../../context';
import { useAnnotatedTaskId } from '../../stores';
import { QUERY_KEYS } from '../../type';
import { Card } from './Card';
import { NoteCreator } from './NoteCreator';
import { MARK_STATUS, STATUS } from './const';
import './index.less';

const { useForm, Item: FormItem } = Form;
const { useApp } = App;

export const NoteList: FC = () => {
  // state
  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);
  const [page, setPage] = useState(1);
  // context
  const readonly = useContext(readonlyContext);
  const editable = !readonly;
  // modal
  const { show } = useModal(NoteCreator);
  const { modal, message } = useApp();
  // shared state
  const [annotatedTaskId, setAnnotatedTaskId] = useAnnotatedTaskId();
  // search params
  const searchParams = useSearchParams()[0];
  const templateCode = searchParams.get('template-code')!;
  // form
  const [form] = useForm<{
    keyword: string;
    status: 0 | 1 | 2 | 3;
    mark_status: 0 | 1 | 2 | 3;
  }>();
  // query
  const {
    data: annotatedList = [],
    isFetching,
    isLoading,
    isSuccess,
  } = useQuery({
    queryKey: [QUERY_KEYS.ANNOTATION_CONFIG, templateCode],
    queryFn: ({ signal }) => getAnnotatedList(templateCode, signal),
    select: (res) => {
      if (res.code === 0) {
        return res.data;
      }
    },
    refetchOnWindowFocus: true,
  });

  const {
    data: taggingList = { records: [], total: 0 },
    refetch,
    isLoading: isTagListLoading,
  } = useQuery({
    queryKey: [
      QUERY_KEYS.ANNOTATION_CONFIG,
      getTaggingList.getQueryKey(),
      {
        templateCode,
        annotatedTaskId,
        page,
      },
    ],
    queryFn: ({ signal }) =>
      getTaggingList(
        { parent_task_id: annotatedTaskId, page, ...form.getFieldsValue() },
        signal,
      ),
    select: (res) => {
      if (res.code === 0) {
        return res.data;
      }
    },
    keepPreviousData: true,
    enabled: annotatedTaskId !== -1,
    refetchInterval: 8000,
  });
  const { mutate: remove, isLoading: isRemoveLoading } = useMutation({
    mutationFn: removeTasks,
    onSuccess(res) {
      if (res.code === 0) {
        refetch();
        message.success('删除成功');
      }
    },
  });

  useEffect(() => {
    const isExist = annotatedList.some(
      (item) => +item?.annotated_task_id === +annotatedTaskId,
    );

    if (isExist) return;
    setAnnotatedTaskId(annotatedList?.[0]?.annotated_task_id || -1);
  }, [annotatedList]);

  if (annotatedList.length === 0) {
    return (
      <section className="nl-fallback__wrapper flex gap-1.5 flex-col justify-center items-center h-4/5">
        <img src={noContent} width={200} alt="no content" />
        <p className="text-sm text-secondary-1">暂无标注</p>
        <p className="text-xs text-secondary-2">
          请先在模板配置页面中添加NLP模型抽取节点
        </p>
      </section>
    );
  }

  const createDeleteModal = (props: ModalFuncProps) =>
    modal.confirm({
      icon: <IconFont type="icon-shibai" />,
      title: '删除确认',
      okButtonProps: {
        danger: true,
      },
      okText: '确认删除',
      ...props,
    });

  return (
    <section className="nl-wrapper flex items-stretch gap-4 px-6 py-4">
      <ul className="flex-2 flex flex-col gap-2 p-2 pb-0 bg-white rounded-lg overflow-auto">
        {isLoading ? (
          <section className="flex flex-col gap-2 justify-center items-center h-4/5">
            <Spin size={24} />
            <p className="text-xs text-secondary-2">正在加载</p>
          </section>
        ) : (
          annotatedList.map((dataSource) => {
            return (
              <Card
                key={dataSource.annotated_task_id}
                dataSource={dataSource}
              />
            );
          })
        )}
      </ul>
      <div className="relative flex-8 px-1 py-5 bg-white rounded-lg overflow-auto">
        <Block title="标注文件列表">
          <section className="flex flex-col gap-5 px-6">
            <div className="sticky -top-3 z-10 flex justify-between py-4 bg-white">
              <Form
                form={form}
                layout="inline"
                onFieldsChange={(changedFields) => {
                  const field = changedFields[0];

                  if ((field.name as string[])[0] !== 'keyword') {
                    refetch();
                  }
                }}
              >
                <FormItem name="keyword">
                  <Input
                    className="w-52"
                    placeholder="请输入文件名称"
                    suffix={<IconFont type="icon-sousuo" />}
                    ref={(ref) => {
                      if (ref?.nativeElement) {
                        ref.nativeElement.onchange = () => refetch();
                      }
                    }}
                  />
                </FormItem>
                <FormItem name="status">
                  <Select
                    allowClear
                    className="!w-40"
                    placeholder="请选择解析状态"
                    options={STATUS}
                  />
                </FormItem>
                <FormItem name="mark_status">
                  <Select
                    allowClear
                    className="!w-40"
                    placeholder="请选择标注状态"
                    options={MARK_STATUS}
                  />
                </FormItem>
              </Form>
              {editable && (
                <Button
                  type="primary"
                  icon={
                    <IconFont className="text-base" type="icon-shangchuan" />
                  }
                  // onClick={() => show({ refresh: () => refetch() })}
                  onClick={() =>
                    NiceModal.show(NoteCreator, { refresh: () => refetch() })
                  }
                >
                  上传文件
                </Button>
              )}
            </div>
            <Table<INote>
              rowSelection={{
                selectedRowKeys,
                onChange(selectedRowKeys) {
                  setSelectedRowKeys(selectedRowKeys as string[]);
                },
              }}
              rowKey={(record) => record.task_code}
              loading={isTagListLoading || isRemoveLoading}
              columns={[
                {
                  title: '序号',
                  dataIndex: 'index',
                  key: 'index',
                  width: '8%',
                  render: (_0, _1, idx) => {
                    return idx + 1;
                  },
                },
                {
                  title: '文件名称',
                  dataIndex: 'task_name',
                  key: 'task_name',
                  ellipsis: true,
                },
                {
                  title: '解析状态',
                  dataIndex: 'status',
                  key: 'status',
                  width: '20%',
                  render: (status) => {
                    switch (status) {
                      case 3: {
                        return '解析失败';
                      }
                      case 2: {
                        return '解析完成';
                      }
                      case 1: {
                        return '解析中';
                      }
                      case 0: {
                        return '任务创建';
                      }
                    }
                  },
                },
                {
                  title: '标注状态',
                  dataIndex: 'mark_status',
                  key: 'mark_status',
                  width: '20%',
                  render: (markStatus) => {
                    const createIcon = (type: string) => (
                      <IconFont className="text-base" type={type} />
                    );
                    switch (markStatus) {
                      case 3: {
                        return (
                          <p className="flex gap-1 items-center text-error">
                            {createIcon('icon-shibai')}
                            <span>无法标注</span>
                          </p>
                        );
                      }
                      case 2: {
                        return (
                          <p className="flex gap-1 items-center text-success">
                            {createIcon('icon-chenggong')}
                            <span>已标注</span>
                          </p>
                        );
                      }
                      case 1: {
                        return (
                          <p className="flex gap-1 items-center text-primary">
                            {createIcon('icon-xinxi')}
                            <span>待确认</span>
                          </p>
                        );
                      }
                      case 0: {
                        return (
                          <p className="flex gap-1 items-center text-warning">
                            {createIcon('icon-jinggao')}
                            <span>未标注</span>
                          </p>
                        );
                      }
                      default: {
                        return <span className="text-secondary-2">-</span>;
                      }
                    }
                  },
                },
                {
                  title: '操作',
                  dataIndex: 'actions',
                  key: 'actions',
                  width: '12%',
                  render: (_, record) => (
                    <section className="flex gap-2 text-secondary-1">
                      {record.mark_status === 3 || record.status !== 2 ? (
                        <IconButton
                          circle
                          title="标注"
                          className="text-base text-secondary-2"
                          iconType="icon-biaozhu1"
                        />
                      ) : (
                        <Link
                          title="标注"
                          to={{
                            pathname: '/note-detail',
                            search: `?parent-id=${annotatedTaskId}&task-id=${record.id}`,
                          }}
                        >
                          <IconButton
                            circle
                            className="text-base text-primary hover:scale-105"
                            iconType="icon-biaozhu1"
                          />
                        </Link>
                      )}
                      <IconButton
                        circle
                        title={
                          record.status === 2 ? '删除' : '解析完成后才能删除'
                        }
                        disabled={
                          readonly || isRemoveLoading || record.status !== 2
                        }
                        className="text-error hover:scale-105"
                        iconType="icon-shanchu1"
                        onClick={() =>
                          createDeleteModal({
                            content: (
                              <>
                                确认删除
                                <span className="text-error font-medium">
                                  {record.task_name}
                                </span>
                                吗
                              </>
                            ),
                            onOk: () => {
                              remove([record.task_code]);
                              if (
                                taggingList.records.length - 1 === 0 &&
                                page > 1
                              ) {
                                setPage(page - 1);
                              }
                            },
                          })
                        }
                      />
                    </section>
                  ),
                },
              ]}
              dataSource={taggingList.records}
              pagination={{
                current: page,
                total: taggingList.total,
                onChange(page) {
                  setPage(page);
                  setSelectedRowKeys([]);
                },
                showTotal: (total) => {
                  const isUndeletable = selectedRowKeys.some((item) =>
                    taggingList.records.some(
                      (record) =>
                        record.task_code === item && record.status !== 2,
                    ),
                  );

                  return (
                    <section className="absolute left-0 flex gap-2 items-center text-sm text-secondary-2">
                      <p>
                        共有{total}条数据，已选中
                        <span className="text-primary font-medium">
                          {selectedRowKeys.length}
                        </span>
                        条
                      </p>
                      <Button
                        danger
                        title={isUndeletable ? '所有文件解析完成才能删除' : ''}
                        disabled={
                          selectedRowKeys.length === 0 ||
                          isRemoveLoading ||
                          isUndeletable
                        }
                        onClick={() =>
                          createDeleteModal({
                            content: (
                              <>
                                确认删除选中的
                                <span className="text-error font-medium">
                                  {selectedRowKeys.length}
                                </span>
                                条任务吗
                              </>
                            ),
                            onOk: () => {
                              remove(selectedRowKeys);
                              if (
                                taggingList.records.length -
                                  selectedRowKeys.length ===
                                  0 &&
                                page > 1
                              ) {
                                setPage(page - 1);
                              }
                              setSelectedRowKeys([]);
                            },
                          })
                        }
                      >
                        批量删除
                      </Button>
                    </section>
                  );
                },
              }}
            />
          </section>
        </Block>
      </div>
    </section>
  );
};
